import {
	acceptCandidate,
	createCallCenterCandidate,
	rejectCandidate,
	sendDocs,
	sendInstructions,
	sendTargets,
	updateCallCenterCandidate,
} from '@/API/repositories/callCenterCandidate';
import { getFileById } from '@/API/repositories/storedDocument';
import { CONSULTANT_MANAGER_ROLES } from '@/common/constants/consultantRoles';
import { CANDIDATE_RECRUITMENT_STATUSES } from '@/common/constants/recruitmentStatuses';
import { formatToDateTamplate } from '@/common/functions/dateFormater';
import formatToGMT from '@/common/functions/formatToGMT';
import { getBase64 } from '@/common/functions/getBase64';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { useFormik } from 'formik';
import { useRef, useState, useContext } from 'react';
import { ROLE_VALUES } from '../../user/components/userForm/constants';
import { MONTH_HOURS } from '../constants/monthHoursOptions';
import { AuthContext } from '@/common/contexts/authContext';

const useCandidateForm = ({
	setShow,
	selectedCandidate,
	callCenterPositionsOptions,
	reload = () => {},
}) => {
	const { makeRequest, hasUnfilledRequest } = useRequestsContext();
	const {
		options: { marketsOptions, productsOptions },
		filterUsersByRoles,
		commonData: { users },
	} = useCommonDataContext();
	const { addMessage } = useMessageQueueContext();
	const { authUser } = useContext(AuthContext);

	const [isFileLoading, setIsFileLoading] = useState(false);
	const [showAreYouSure, setShowAreYouSure] = useState(false);
	const [showAccept, setShowAccept] = useState(false);
	const [showDocs, setShowDocs] = useState(false);
	const [showInstructions, setShowInstructions] = useState(false);

	const inputsRef = useRef();
	const cvRef = useRef();

	const user = authUser;
	const userOption = {
		label: user.username,
		value: user.id,
	};
	const userRole = user.role;
	const selectCallCenterManagerDisabled =
		ROLE_VALUES.CALL_CENTER_MANAGER === userRole ? true : false;
	const showProductSelectInput =
		ROLE_VALUES.CALL_CENTER_MANAGER !== userRole ? true : false;

	const hasUserFullAccess = [
		ROLE_VALUES.HEAD_CC_MANAGER,
		ROLE_VALUES.ADMIN,
		ROLE_VALUES.CALL_CENTER_MANAGER,
	].includes(userRole);
	const isNotRejected =
		selectedCandidate.status !== CANDIDATE_RECRUITMENT_STATUSES.REJECTED;
	const showAllButtons =
		selectedCandidate?._id && isNotRejected && hasUserFullAccess;
	const showSendDocsButton = [
		ROLE_VALUES.HEAD_CC_MANAGER,
		ROLE_VALUES.ADMIN,
	].includes(userRole);

	const showSendInstructionsButton = selectedCandidate.product ? true : false;

	const showSubmitButton =
		selectedCandidate.status !== CANDIDATE_RECRUITMENT_STATUSES.REJECTED;

	const selectInitialCallCenterManager = () => {
		return userRole === ROLE_VALUES.CALL_CENTER_MANAGER
			? userOption
			: filterUsersByRoles(CONSULTANT_MANAGER_ROLES).find(
					(o) => o.value === selectedCandidate?.call_center_manager,
			  ) || [];
	};

	const formik = useFormik({
		initialValues: {
			first_name: selectedCandidate?.first_name || '',
			last_name: selectedCandidate?.last_name || '',
			phone: selectedCandidate?.phone || '',
			email: selectedCandidate?.email || '',
			market:
				marketsOptions.find((o) => o.value === selectedCandidate?.market) || [],
			product:
				productsOptions?.find((o) => o.value === selectedCandidate?.product) ||
				[],
			call_center_position:
				callCenterPositionsOptions.find(
					(position) =>
						selectedCandidate?.call_center_position === position.value,
				) || '',
			call_center_manager: selectInitialCallCenterManager(),
			cv: null,
			has_headset: selectedCandidate?.has_headset,
			month_hours: MONTH_HOURS.find(
				(h) => h.value === selectedCandidate.month_hours,
			),
			possible_start_date:
				selectedCandidate?.possible_start_date &&
				formatToDateTamplate(selectedCandidate.possible_start_date),
		},
		onSubmit: async (values) => handleSubmit(await foramtBeforeSubmit(values)),
	});

	const foramtBeforeSubmit = async (values) => {
		const formattedData = {
			...values,
			market: values.market.value,
			product: values.product.value,
			call_center_manager: values.call_center_manager.value,
			call_center_position: values.call_center_position.value,
			month_hours: values.month_hours?.value,
			possible_start_date:
				values.possible_start_date && formatToGMT(values.possible_start_date),
			cv: values.cv?.length && (await getBase64(values.cv?.[0])),
			has_headset: !!formik.values.has_headset,
		};
		if (!values.product.value) {
			delete formattedData.product;
		}

		if (!values.month_hours?.value) {
			delete formattedData.month_hours;
		}

		return formattedData;
	};

	const getLogs = () => {
		return selectedCandidate.logs?.map((log) => {
			const qwe = users.find((user) => user._id === log.user);
			return { ...log, user: qwe.username };
		});
	};

	const handleSubmit = async (payload) => {
		const formData = new FormData();
		Object.entries(payload).forEach(([key, value]) => {
			formData.append(key, value);
		});
		formData.append('cv', cvRef.current?.files[0]);

		const requestFn = selectedCandidate?._id
			? updateCallCenterCandidate
			: createCallCenterCandidate;

		const response = await makeRequest(
			requestFn.bind(null, formData, selectedCandidate?._id),
		);

		if (!response?.data) {
			return addMessage('Something went wrong', 'error');
		}

		addMessage('Success', 'success');
		setShow(false);
		reload();
	};

	const handleInstructions = async (payload) => {
		if (payload.fileIds.length === 0) {
			return addMessage('No files to send.', 'error');
		}
		const response = await makeRequest(
			sendInstructions.bind(null, payload, selectedCandidate?._id),
		);

		if (!response?.data) {
			return addMessage('Something went wrong', 'error');
		}
		addMessage('Success', 'success');
		setShowInstructions(false);
	};

	const handleDocs = async (payload) => {
		if (payload.files.length === 0) {
			return addMessage('No files to send.', 'error');
		}

		const response = await makeRequest(
			sendDocs.bind(null, payload, selectedCandidate?._id),
		);

		if (!response?.data) {
			return addMessage('Something went wrong', 'error');
		}
	};

	const handleAccept = async (date) => {
		const response = await makeRequest(
			acceptCandidate.bind(null, date, selectedCandidate?._id),
		);
		if (!response?.data) {
			return addMessage('Something went wrong', 'error');
		}

		addMessage('Success', 'success');
		setShowAccept(false);
	};

	const handleGetStoredDocument = async () => {
		setIsFileLoading(() => true);

		const response = await makeRequest(
			getFileById.bind(null, selectedCandidate.cv),
		);

		if (response.data) {
			const url = window.URL.createObjectURL(
				new Blob([new Uint8Array(response.data.data.data).buffer]),
			);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', response.data.name);
			document.body.appendChild(link);
			link.click();
		}
		setIsFileLoading(() => false);
	};

	const handleReject = async () => {
		const response = await makeRequest(
			rejectCandidate.bind(null, selectedCandidate._id),
		);

		if (!response?.data) {
			return addMessage('Something went wrong', 'error');
		}

		addMessage('Success', 'success');
		setShow(false);
		reload();
	};

	const handleSendTargets = async () => {
		const response = await makeRequest(
			sendTargets.bind(null, selectedCandidate._id),
		);

		if (!response?.data) {
			return addMessage('Something went wrong', 'error');
		}

		addMessage('Success', 'success');
		setShow(false);
	};

	const isLoading = hasUnfilledRequest(
		createCallCenterCandidate,
		updateCallCenterCandidate,
		rejectCandidate,
	);

	return {
		isLoading,
		formik,
		inputsRef,
		marketsOptions,
		productsOptions,
		filterUsersByRoles,
		handleGetStoredDocument,
		isFileLoading,
		setShowAreYouSure,
		showAreYouSure,
		handleReject,
		getLogs,
		showAccept,
		setShowAccept,
		showDocs,
		setShowDocs,
		handleDocs,
		handleAccept,
		handleSendTargets,
		showInstructions,
		setShowInstructions,
		handleInstructions,
		cvRef,
		showAllButtons,
		showSendDocsButton,
		showSubmitButton,
		setShow,
		selectedCandidate,
		callCenterPositionsOptions,
		reload,
		selectCallCenterManagerDisabled,
		showProductSelectInput,
		showSendInstructionsButton,
	};
};

export default useCandidateForm;
